/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-floating-promises */

import { useCallback, useContext } from 'react';
import { getExactKeyPath, getKeyPath, isLogicGroup, removeEmptyChildGroups, removeKeyPath } from 'customer-data-filters-ui/filterQueryFormat/logic/LogicGroup';
import { useOnChangeHandlers } from './useOnChangeHandlers';
import SegmentationFiltersInstanceContext from '../state/SegmentationFiltersInstanceContext';
import FilterUserAction from 'customer-data-filters-ui/filterQueryFormat/FilterUserAction';
import { USED_SEGMENTATION_FILTERS } from 'customer-data-filters-ui/filterQueryFormat/UsageActionTypes';
import { VIEWER_CONDITION_ADDED, AND_GROUP_ADDED, AND_GROUP_CLONED, AND_GROUP_DELETED, VIEWER_CONDITION_DELETED, VIEWER_CONDITION_EDIT, TEST_AN_OBJECT_OPENED, TEST_AN_OBJECT_CLOSED } from 'customer-data-filters-ui/filterQueryFormat/UsageSubActionTypes';
import FilterLogicGroup from '../filterQueryFormat/logic/FilterLogicGroup';
import { WILL_CANCEL_CHANGES, WILL_DISCARD_CHANGES, WILL_EDIT_FILTERS, CANCELED_CHANGES, DISCARDED_CHANGES, EDITED_FILTERS, CONFIRMED_CHANGES, WILL_CONFIRM_CHANGES } from '../constants/EditModeActionTypes';
import { useLoadMoreFieldsHandler } from './useLoadMoreFieldsHandler';
import { ObjectTypeIdRegexStrict, isAnyEventTypeId } from 'customer-data-filters-ui/filterQueryFormat/ObjectTypeId';
import { useValidateCondition } from './useValidateCondition';
import { List, OrderedMap } from 'immutable';
import { makeConditionHashKey, shouldShowNullValueCheckbox } from './useNullValueState';
import { isOperator } from '../filterQueryFormat/operator/Operator';
const getSelectedObjectTypeFromConditionPath = (baseFilterFamily, conditionPath) => {
  const conditionPathFilterFamily = conditionPath.size > 1 ? conditionPath.get(1) : '';
  if (typeof conditionPathFilterFamily === 'string') {
    const possibleFilterFamily = conditionPathFilterFamily.split('__').shift() || '';
    const isAssociation = ObjectTypeIdRegexStrict.test(possibleFilterFamily);
    const filterFamily = isAssociation ? possibleFilterFamily : baseFilterFamily;
    return {
      filterFamily,
      isObjectOwnFilterBranch: !isAssociation
    };
  }
  return {
    filterFamily: baseFilterFamily,
    isObjectOwnFilterBranch: true
  };
};
const getAllConditionPaths = (logicGroup, basePath = List()) => {
  const result = [];
  if (!isLogicGroup(logicGroup)) {
    return result;
  }
  logicGroup.conditions.entrySeq().forEach(([key, condition]) => {
    const currentPath = basePath.push(key);
    if (isLogicGroup(condition)) {
      // Recursively traverse nested logic groups
      result.push(...getAllConditionPaths(condition, currentPath));
    } else if (isOperator(condition)) {
      // Add leaf condition
      result.push({
        condition,
        conditionPath: currentPath,
        filterFamily: logicGroup.filterFamily
      });
    }
  });
  return result;
};

// for use when all filters are shown in edit mode, so the null value state is initialized all at once
const getInitializedNullValueState = (nullValueState, internalDraftValue) => {
  const newNullValueState = Object.assign({}, nullValueState);

  // Get all condition paths from the internal draft value
  const allConditionPaths = getAllConditionPaths(internalDraftValue);

  // Initialize null value state for each condition
  allConditionPaths.forEach(({
    condition,
    conditionPath,
    filterFamily
  }) => {
    const conditionHashKey = makeConditionHashKey(condition, conditionPath);
    const cachedShowIncludeObjectsWithNoValueSet = nullValueState.showIncludeObjectsWithNoValueSetByOperator[conditionHashKey];
    if (cachedShowIncludeObjectsWithNoValueSet === undefined) {
      newNullValueState.showIncludeObjectsWithNoValueSetByOperator = Object.assign({}, newNullValueState.showIncludeObjectsWithNoValueSetByOperator, {
        [conditionHashKey]: shouldShowNullValueCheckbox(filterFamily, condition)
      });
      newNullValueState.defaultNullValueByOperator = Object.assign({}, newNullValueState.defaultNullValueByOperator, {
        [conditionHashKey]: condition.defaultNullValue
      });
    }
  });

  // Mark as initialized
  newNullValueState.isInitialized = true;
  return newNullValueState;
};
export const useViewerActions = () => {
  const {
    dispatch,
    internalDraftValue,
    externalOriginalValue,
    getTrackerInstance,
    nullValueState,
    props: {
      onUserAction,
      onChange,
      onBeforeEditModeChange,
      onEditModeChange,
      baseFilterFamily,
      useOnePanel,
      showAllFiltersInEditMode
    }
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    handleOnChange
  } = useOnChangeHandlers();
  const {
    validateLogicGroup
  } = useValidateCondition();
  const loadMoreFields = useLoadMoreFieldsHandler();
  const onAddFilter = useCallback((parentGroupKeyPath, filterFamily) => {
    const filterFamilyInfo = filterFamily ? {
      filterFamily,
      isObjectOwnFilterBranch: false
    } : undefined;
    dispatch({
      type: 'addFilter',
      payload: {
        parentGroupKeyPath,
        filterFamilyInfo,
        selectedObjectType: getSelectedObjectTypeFromConditionPath(baseFilterFamily, parentGroupKeyPath)
      }
    });
    if (filterFamily) {
      setTimeout(() => {
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 3 arguments, but got 1.
        loadMoreFields(filterFamily).then(() => dispatch({
          type: 'fieldsLoaded'
        }));
      }, 0);
    }
    onUserAction(FilterUserAction({
      action: USED_SEGMENTATION_FILTERS,
      subAction: VIEWER_CONDITION_ADDED
    }));
  }, [dispatch, baseFilterFamily, onUserAction, loadMoreFields]);
  const onEditFilter = useCallback((conditionPath, filterFamily) => {
    var _getTrackerInstance;
    dispatch({
      type: 'editFilter',
      payload: {
        conditionPath,
        filterFamily
      }
    });
    (_getTrackerInstance = getTrackerInstance()) === null || _getTrackerInstance === void 0 || _getTrackerInstance.track('editedCondition', {
      primaryObjectTypeId: baseFilterFamily,
      version: useOnePanel ? '2' : '1'
    });
    onUserAction(FilterUserAction({
      action: USED_SEGMENTATION_FILTERS,
      subAction: VIEWER_CONDITION_EDIT
    }));
  }, [baseFilterFamily, dispatch, getTrackerInstance, onUserAction, useOnePanel]);
  const onAddFilterGroup = useCallback(() => {
    var _getTrackerInstance2;
    dispatch({
      type: 'addFilterGroup',
      payload: baseFilterFamily
    });
    (_getTrackerInstance2 = getTrackerInstance()) === null || _getTrackerInstance2 === void 0 || _getTrackerInstance2.track('addedFilterGroup', {
      primaryObjectTypeId: baseFilterFamily,
      type: 'AND',
      version: useOnePanel ? '2' : '1'
    });
    onUserAction(FilterUserAction({
      action: USED_SEGMENTATION_FILTERS,
      subAction: AND_GROUP_ADDED
    }));
  }, [dispatch, baseFilterFamily, getTrackerInstance, onUserAction, useOnePanel]);
  const onAddFirstFilterGroup = useCallback(() => {
    const addFirstFilterGroup = () => {
      onAddFilterGroup();
      onEditModeChange(EDITED_FILTERS);
    };
    if (onBeforeEditModeChange) {
      onBeforeEditModeChange(WILL_EDIT_FILTERS).then(shouldAddFilterGroup => {
        if (shouldAddFilterGroup) {
          addFirstFilterGroup();
        }
      });
    } else {
      addFirstFilterGroup();
    }
  }, [onAddFilterGroup, onBeforeEditModeChange, onEditModeChange]);
  const onCloneFilterGroup = useCallback(groupIndexNumber => {
    var _getTrackerInstance3;
    const viewerClonedGroupIndex = groupIndexNumber + 1;
    const newInternalDraftValue = FilterLogicGroup.insertAtPosition(viewerClonedGroupIndex, getExactKeyPath(List([groupIndexNumber]), internalDraftValue), removeEmptyChildGroups(internalDraftValue));
    dispatch({
      type: 'viewerClonedGroupIndex',
      payload: {
        viewerClonedGroupIndex,
        internalDraftValue: newInternalDraftValue
      }
    });
    handleOnChange(newInternalDraftValue);
    (_getTrackerInstance3 = getTrackerInstance()) === null || _getTrackerInstance3 === void 0 || _getTrackerInstance3.track('clonedFilterGroup', {
      primaryObjectTypeId: baseFilterFamily,
      type: 'AND',
      version: useOnePanel ? '2' : '1'
    });
    onUserAction(FilterUserAction({
      action: USED_SEGMENTATION_FILTERS,
      subAction: AND_GROUP_CLONED
    }));
  }, [internalDraftValue, dispatch, handleOnChange, getTrackerInstance, onUserAction, baseFilterFamily, useOnePanel]);
  const onDeleteFilterGroup = useCallback(groupIndexNumber => {
    var _getTrackerInstance4;
    let newInternalDraftValue = removeKeyPath(List([groupIndexNumber]), removeEmptyChildGroups(internalDraftValue));

    // Resequence the remaining groups after removing the group
    newInternalDraftValue = newInternalDraftValue.update('conditions', conditions => {
      const entries = conditions.valueSeq().toArray();
      return OrderedMap(entries.map((value, i) => [`${i}`, value]));
    });
    dispatch({
      type: 'resetEditor',
      payload: {
        internalDraftValue: newInternalDraftValue,
        shouldLeaveEditorOpen: true
      }
    });
    handleOnChange(newInternalDraftValue);
    (_getTrackerInstance4 = getTrackerInstance()) === null || _getTrackerInstance4 === void 0 || _getTrackerInstance4.track('deletedFilterGroup', {
      primaryObjectTypeId: baseFilterFamily,
      type: 'AND',
      version: useOnePanel ? '2' : '1'
    });
    onUserAction(FilterUserAction({
      action: USED_SEGMENTATION_FILTERS,
      subAction: AND_GROUP_DELETED
    }));
  }, [baseFilterFamily, dispatch, getTrackerInstance, handleOnChange, internalDraftValue, onUserAction, useOnePanel]);
  const onConditionDeleteCancel = useCallback(() => {
    dispatch({
      type: 'viewerConditionToDelete',
      payload: undefined
    });
  }, [dispatch]);
  const onConditionDeleteConfirm = useCallback(conditionPath => {
    var _getTrackerInstance5;
    const conditionToDeleteParentGroup = getKeyPath(conditionPath.skipLast(1), internalDraftValue);
    const removeEmptyParentGroup = !isAnyEventTypeId(conditionToDeleteParentGroup.filterFamily);
    const newInternalDraftValue = validateLogicGroup(removeKeyPath(conditionPath, removeEmptyChildGroups(internalDraftValue), removeEmptyParentGroup));
    dispatch({
      type: 'deleteCondition',
      payload: {
        internalDraftValue: newInternalDraftValue
      }
    });
    handleOnChange(newInternalDraftValue);
    (_getTrackerInstance5 = getTrackerInstance()) === null || _getTrackerInstance5 === void 0 || _getTrackerInstance5.track('deletedCondition', {
      primaryObjectTypeId: baseFilterFamily,
      version: useOnePanel ? '2' : '1'
    });
    onUserAction(FilterUserAction({
      action: USED_SEGMENTATION_FILTERS,
      subAction: VIEWER_CONDITION_DELETED
    }));
  }, [baseFilterFamily, dispatch, getTrackerInstance, handleOnChange, internalDraftValue, onUserAction, validateLogicGroup, useOnePanel]);
  const onConditionDeleteHover = useCallback(conditionPath => {
    dispatch({
      type: 'viewerConditionToDeleteHovered',
      payload: conditionPath
    });
  }, [dispatch]);
  const onConditionDeleteTrigger = useCallback(conditionPath => {
    const conditionToDelete = getExactKeyPath(conditionPath, internalDraftValue);
    if (isLogicGroup(conditionToDelete)) {
      dispatch({
        type: 'viewerConditionToDelete',
        payload: conditionPath
      });
    } else {
      onConditionDeleteConfirm(conditionPath);
    }
  }, [dispatch, internalDraftValue, onConditionDeleteConfirm]);
  const onHighlightCondition = useCallback(conditionId => {
    dispatch({
      type: 'viewerConditionIdToHighlight',
      payload: conditionId
    });
  }, [dispatch]);
  const onEditFilters = useCallback(() => {
    const editFilters = () => {
      var _getTrackerInstance6;
      if (!nullValueState.isInitialized && showAllFiltersInEditMode) {
        const newNullValueState = getInitializedNullValueState(nullValueState, internalDraftValue);
        dispatch({
          type: 'nullValueState',
          payload: newNullValueState
        });
      }
      dispatch({
        type: 'editFilters'
      });
      onEditModeChange(EDITED_FILTERS);
      (_getTrackerInstance6 = getTrackerInstance()) === null || _getTrackerInstance6 === void 0 || _getTrackerInstance6.track('clickedEditFilters', {
        primaryObjectTypeId: baseFilterFamily,
        version: useOnePanel ? '2' : '1'
      });
    };
    if (onBeforeEditModeChange) {
      onBeforeEditModeChange(WILL_EDIT_FILTERS).then(shouldEnterEditMode => {
        if (shouldEnterEditMode) {
          editFilters();
        }
      });
    } else {
      editFilters();
    }
  }, [baseFilterFamily, dispatch, getTrackerInstance, onBeforeEditModeChange, onEditModeChange, useOnePanel, internalDraftValue, nullValueState, showAllFiltersInEditMode]);
  const onCancelChanges = useCallback(() => {
    const cancelChanges = () => {
      dispatch({
        type: 'discardChanges'
      });
      onChange(externalOriginalValue, {
        fromDiscardingChanges: true
      });
      onEditModeChange(CANCELED_CHANGES);
    };
    if (onBeforeEditModeChange) {
      onBeforeEditModeChange(WILL_CANCEL_CHANGES).then(shouldCancelChanges => {
        if (shouldCancelChanges) {
          cancelChanges();
        }
      });
    } else {
      cancelChanges();
    }
  }, [dispatch, externalOriginalValue, onBeforeEditModeChange, onChange, onEditModeChange]);
  const onDiscardChanges = useCallback(() => {
    const discardChanges = () => {
      var _getTrackerInstance7;
      dispatch({
        type: 'discardChanges'
      });
      onChange(externalOriginalValue, {
        fromDiscardingChanges: true
      });
      onEditModeChange(DISCARDED_CHANGES);
      (_getTrackerInstance7 = getTrackerInstance()) === null || _getTrackerInstance7 === void 0 || _getTrackerInstance7.track('discardedChanges', {
        primaryObjectTypeId: baseFilterFamily,
        version: useOnePanel ? '2' : '1'
      });
    };
    if (onBeforeEditModeChange) {
      onBeforeEditModeChange(WILL_DISCARD_CHANGES).then(shouldDiscardChanges => {
        if (shouldDiscardChanges) {
          discardChanges();
        }
      });
    } else {
      discardChanges();
    }
  }, [onBeforeEditModeChange, dispatch, onChange, externalOriginalValue, onEditModeChange, getTrackerInstance, baseFilterFamily, useOnePanel]);
  const onConfirmChanges = useCallback(() => {
    const confirmChanges = () => {
      const newInternalDraftValue = removeEmptyChildGroups(internalDraftValue);
      dispatch({
        type: 'resetEditor',
        payload: {
          internalDraftValue: newInternalDraftValue
        }
      });
      handleOnChange(newInternalDraftValue);
      onEditModeChange(CONFIRMED_CHANGES);
    };
    if (onBeforeEditModeChange) {
      onBeforeEditModeChange(WILL_CONFIRM_CHANGES).then(shouldConfirmChanges => {
        if (shouldConfirmChanges) {
          confirmChanges();
        }
      });
    } else {
      confirmChanges();
    }
  }, [dispatch, handleOnChange, onBeforeEditModeChange, onEditModeChange, internalDraftValue]);
  const onUpdateFilterSelected = useCallback(newViewerFilterSelected => {
    dispatch({
      type: 'viewerFilterSelected',
      payload: newViewerFilterSelected
    });
  }, [dispatch]);
  const onShowTestFilters = useCallback(() => {
    var _getTrackerInstance8;
    dispatch({
      type: 'showTestFilters'
    });
    (_getTrackerInstance8 = getTrackerInstance()) === null || _getTrackerInstance8 === void 0 || _getTrackerInstance8.track('openedTestARecord', {
      primaryObjectTypeId: baseFilterFamily,
      version: useOnePanel ? '2' : '1'
    });
    onUserAction(FilterUserAction({
      action: USED_SEGMENTATION_FILTERS,
      subAction: TEST_AN_OBJECT_OPENED
    }));
  }, [baseFilterFamily, dispatch, getTrackerInstance, onUserAction, useOnePanel]);
  const onHideTestFilters = useCallback(() => {
    var _getTrackerInstance9;
    dispatch({
      type: 'hideTestFilters'
    });
    (_getTrackerInstance9 = getTrackerInstance()) === null || _getTrackerInstance9 === void 0 || _getTrackerInstance9.track('closedTestARecord', {
      primaryObjectTypeId: baseFilterFamily,
      version: useOnePanel ? '2' : '1'
    });
    onUserAction(FilterUserAction({
      action: USED_SEGMENTATION_FILTERS,
      subAction: TEST_AN_OBJECT_CLOSED
    }));
  }, [baseFilterFamily, dispatch, getTrackerInstance, onUserAction, useOnePanel]);
  const onTestFiltersObjectChange = useCallback(objectId => {
    dispatch({
      type: 'testFiltersObjectChanged',
      payload: objectId
    });
  }, [dispatch]);
  const onTestFiltersUpdate = useCallback(result => {
    dispatch({
      type: 'onTestFiltersUpdated',
      payload: result
    });
  }, [dispatch]);
  return {
    onAddFilter,
    onEditFilter,
    onAddFilterGroup,
    onAddFirstFilterGroup,
    onCloneFilterGroup,
    onDeleteFilterGroup,
    onConditionDeleteConfirm,
    onConditionDeleteCancel,
    onConditionDeleteHover,
    onConditionDeleteTrigger,
    onHighlightCondition,
    onEditFilters,
    onCancelChanges,
    onDiscardChanges,
    onConfirmChanges,
    onUpdateFilterSelected,
    onShowTestFilters,
    onHideTestFilters,
    onTestFiltersObjectChange,
    onTestFiltersUpdate
  };
};